import {DrawerFooter} from "@/widget/DrawerModal/DrawerModal";
import TextField from "@mui/material/TextField";
import {Controller, useForm} from "react-hook-form";
import {Autocomplete, FormLabel, Grid} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import React, {FC} from "react"
import Box from "@mui/material/Box";
import {useAllDict} from "@/store/dict.c";
import {useDrawerModal, useGlobalActions} from "@/store/global.c";
import * as z from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {emptyValue} from "@/helper/method";
import {useCreateReview} from "@/service/review.s";
import {ShowChartOutlined} from "@mui/icons-material";
import {isNumber} from "lodash";
import {DictPO} from "@/model/system";

const validationSchema = z.object({
    status: z.number({message: "请选择审核结果"}),
    opinion: z.ostring(),
    planRatio: z.number({message: "请填写任务进度"}).max(100, "任务进度不能大于100").or(z.string().default('')),
    taskRatio: z.number({message: "请填写任务进度"}).max(100, "任务进度不能大于100").or(z.string().default('')),
    planStatusId: z.number({message: "请选择目标状态"}).or(z.string().default('')),
    taskStatusId: z.number({message: "请选择任务状态"}).or(z.string().default('')),
    taskStatus: z.string().optional(),
    planStatus: z.string().optional(),
    tag: z.number().default(0),
    targetId: z.number().optional().default(0),
}).superRefine((values, ctx) => {
    if ((values.tag == 3 || values.tag == 1) && !isNumber(values.planRatio)) {
        ctx.addIssue(
            {code: z.ZodIssueCode.custom, message: "请填写目标进度", path: ['planRatio']}
        )
    }
    if ((values.tag == 3 || values.tag == 1) && !isNumber(values.taskRatio)) {
        ctx.addIssue(
            {code: z.ZodIssueCode.custom, message: "请填写任务进度", path: ['taskRatio']}
        )
    }
    if (values.tag == 3 && !isNumber(values.taskStatusId)) {
        ctx.addIssue(
            {code: z.ZodIssueCode.custom, message: "请选择任务状态", path: ['taskStatusId']}
        )
    }
    if (values.tag == 3 && !isNumber(values.planStatusId)) {
        ctx.addIssue(
            {code: z.ZodIssueCode.custom, message: "请选择目标状态", path: ['planStatusId']}
        )
    }
});

export type TaskReviewForm = z.infer<typeof validationSchema>

const defaultValues = emptyValue(validationSchema)

interface TaskReviewProps {
    tag: number // 1.进度汇报 2.工时登记 3.任务验收
}

export const TaskReview: FC<TaskReviewProps> = ({tag}) => {
    const allDict = useAllDict()
    const {reviewStatus, taskStatus, planStatus} = allDict
    const {setToast} = useGlobalActions()
    const {mutateAsync: createReviewMutate, isPending: createReviewPending} = useCreateReview()
    const {setDrawerModal, setDialogModal} = useGlobalActions()
    const {id: drawerId, onSearch} = useDrawerModal()
    const {
        register,
        control,
        handleSubmit,
        setValue,
        reset,
        formState: {errors},
    } = useForm<TaskReviewForm>({
        resolver: zodResolver(validationSchema),
        values: {tag} as TaskReviewForm,
        defaultValues,
    });

    const onSubmit = async (values: TaskReviewForm) => {
        const result = await createReviewMutate({...values, tag, targetId: drawerId!})
        if (result.code == 0) {
            setDrawerModal({open: false})
            setDialogModal({open: false})
            onSearch && onSearch()
        } else {
            setToast({open: true, message: result.msg, severity: "error", variant: 'standard'})
        }
    }

    return (
        <Box component={'form'} noValidate onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
                {tag == 3 && <> <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>目标状态</FormLabel>
                        <Controller control={control} render={({field: {onChange}}) => <Autocomplete
                            options={planStatus}
                            onChange={(_, option: DictPO | null) => {
                                setValue("planStatusId", option!.value)
                                onChange(option?.label)
                            }}
                            fullWidth
                            renderInput={(params) => (
                                <TextField error={!!errors?.planStatusId}
                                           helperText={errors?.planStatusId?.message}
                                           variant="outlined"
                                           placeholder={"选择目标状态"} {...params} />
                            )}
                        />} name={"planStatus"}/>
                    </FormControl>
                </Grid>
                    <Grid size={6}>
                        <FormControl fullWidth={true} required={true}>
                            <FormLabel>任务状态</FormLabel>
                            <Controller control={control} render={({field: {onChange}}) => {
                                return <Autocomplete
                                    options={taskStatus}
                                    onChange={(_, option: DictPO | null) => {
                                        setValue("taskStatusId", option!.value)
                                        onChange(option?.label)
                                    }}
                                    fullWidth
                                    renderInput={(params) => (
                                        <TextField error={!!errors?.taskStatusId}
                                                   helperText={errors?.taskStatusId?.message}
                                                   variant="outlined"
                                                   placeholder={"选择任务状态"} {...params} />
                                    )}
                                />
                            }} name={"taskStatus"}/>
                        </FormControl>
                    </Grid></>}
                {(tag == 3 || tag == 1) && <><Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel>目标进度</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <TextField
                            error={!!errors?.planRatio}
                            helperText={errors?.planRatio?.message}
                            onChange={event => onChange(parseInt(event.target.value) || '')}
                            slotProps={{
                                input: {
                                    inputProps: {
                                        min: 0,
                                        max: 1000,
                                        maxLength: 3, inputMode: 'numeric'
                                    },
                                    endAdornment: <ShowChartOutlined color="disabled"/>,
                                },
                            }}
                            placeholder="填写目标进度"
                            variant="outlined"
                        />} name={'planRatio'}/>
                    </FormControl>
                </Grid>
                    <Grid size={6}>
                        <FormControl fullWidth={true} autoFocus={true} required={true}>
                            <FormLabel>任务进度</FormLabel>
                            <Controller control={control} render={({field: {onChange, value}}) => <TextField
                                error={!!errors?.taskRatio}
                                helperText={errors?.taskRatio?.message}
                                onChange={event => onChange(parseInt(event.target.value) || '')}
                                slotProps={{
                                    input: {
                                        inputProps: {
                                            min: 0,
                                            max: 1000,
                                            maxLength: 3, inputMode: 'numeric', pattern: '[0-9]*'
                                        },
                                        endAdornment: <ShowChartOutlined color="disabled"/>,
                                    },
                                }}
                                placeholder="填写任务进度"
                                variant="outlined"
                            />} name={'taskRatio'}/>
                        </FormControl>
                    </Grid> </>}

                <Grid size={12}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>审核结果</FormLabel>
                        <Controller control={control} render={({field: {onChange}}) => <Autocomplete
                            options={reviewStatus}
                            fullWidth
                            onChange={(event, value) => {
                                onChange(value)
                                setValue("status", value!.value)
                            }}
                            renderInput={(params) => (
                                <TextField error={!!errors?.status}
                                           helperText={errors?.status?.message}
                                           variant="outlined"
                                           placeholder={"选择审核结果"} {...params} />
                            )}
                        />} name={"status"}/>
                    </FormControl>
                </Grid>
                <Grid size={12}>
                    <FormControl fullWidth={true}>
                        <FormLabel>审核意见</FormLabel>
                        <TextField
                            multiline
                            rows={3}
                            {...register("opinion")}
                            slotProps={{
                                input: {
                                    sx: {height: 'auto', minHeight: 80},
                                },
                            }}
                            placeholder="审核意见"
                            fullWidth
                        />
                    </FormControl>
                </Grid>
            </Grid>
            <DrawerFooter loading={createReviewPending} onReset={() => reset(defaultValues)}/>
        </Box>
    );
};
